import { Link, useNavigate } from "react-router-dom";
import { useShop } from "@/store/ShopContext";
import { currencySymbol, formatPrice } from "@/pages/home/components/ProductCard";

const FREE_SHIPPING_THRESHOLD = 199;

export default function CartDrawer() {
  const {
    cart,
    cartOpen,
    closeCart,
    cartCount,
    cartSubtotal,
    updateQuantity,
    removeFromCart,
    clearCart,
  } = useShop();

  const currency = cart[0]?.currency ?? "BRL";
  const symbol = currencySymbol(currency);
  const navigate = useNavigate();

  const remainingForFree = Math.max(0, FREE_SHIPPING_THRESHOLD - cartSubtotal);
  const progressPct = Math.min(
    100,
    Math.round((cartSubtotal / FREE_SHIPPING_THRESHOLD) * 100),
  );

  const onCheckout = () => {
    closeCart();
    navigate("/checkout");
  };

  const onClear = () => {
    if (cart.length === 0) return;
    clearCart();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closeCart}
        aria-hidden={!cartOpen}
        className={`fixed inset-0 z-[60] bg-foreground-950/40 transition-opacity duration-300 ${
          cartOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      ></div>

      {/* Painel do carrinho */}
      <aside
        role="dialog"
        aria-label="Carrinho de compras"
        aria-hidden={!cartOpen}
        className={`fixed top-0 right-0 z-[61] h-full w-full sm:w-[440px] bg-background-50 flex flex-col transition-transform duration-300 ease-out ${
          cartOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Cabeçalho */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
          <div className="flex items-baseline gap-2">
            <h2 className="font-heading text-foreground-950 text-xl">Carrinho</h2>
            {cartCount > 0 && (
              <span className="text-[13px] text-foreground-500">
                {cartCount} {cartCount === 1 ? "item" : "itens"}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={closeCart}
            aria-label="Fechar carrinho"
            className="w-9 h-9 rounded-full flex items-center justify-center text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-[20px]"></i>
          </button>
        </div>

        {/* Estado vazio */}
        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-shopping-basket-2-line text-foreground-700 text-[28px]"></i>
            </div>
            <p className="font-heading text-foreground-950 text-lg mb-1">
              Seu carrinho está vazio
            </p>
            <p className="text-[13px] text-foreground-600 mb-6 max-w-xs">
              Que tal encontrar uma peça feita à mão?
            </p>
            <button
              type="button"
              onClick={closeCart}
              className="px-5 py-2.5 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Continuar navegando
            </button>
          </div>
        ) : (
          <>
            {/* Progresso do frete grátis */}
            <div className="px-5 pt-4 pb-3 border-b border-background-200/60">
              {remainingForFree > 0 ? (
                <p className="text-[12.5px] text-foreground-700 mb-2">
                  Adicione{" "}
                  <span className="font-semibold text-foreground-950">
                    {symbol}
                    {formatPrice(remainingForFree)}
                  </span>{" "}
                  para ganhar frete grátis
                </p>
              ) : (
                <p className="text-[12.5px] text-foreground-950 font-medium mb-2 inline-flex items-center gap-1.5">
                  <i className="ri-checkbox-circle-fill text-accent-600"></i>
                  Você ganhou frete grátis
                </p>
              )}
              <div className="h-1.5 rounded-full bg-background-200 overflow-hidden">
                <div
                  className="h-full bg-foreground-950 rounded-full transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                ></div>
              </div>
            </div>

            {/* Itens */}
            <div className="flex-1 overflow-y-auto px-5">
              <ul className="divide-y divide-background-200/70">
                {cart.map((line) => {
                  const lineSymbol = currencySymbol(line.currency);
                  const hasDiscount = line.originalPrice > line.unitPrice;
                  const atMax = line.quantity >= line.maxStock;
                  return (
                    <li key={line.id} className="py-4 flex gap-3">
                      <Link
                        to={`/product/${line.productId}`}
                        onClick={closeCart}
                        className="shrink-0 w-[72px] h-[72px] rounded-lg overflow-hidden bg-background-100 cursor-pointer"
                      >
                        {line.image && (
                          <img
                            src={line.image}
                            alt={line.productName}
                            className="w-full h-full object-cover object-top"
                          />
                        )}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <Link
                            to={`/product/${line.productId}`}
                            onClick={closeCart}
                            className="text-[14px] text-foreground-950 leading-snug line-clamp-2 hover:underline cursor-pointer"
                          >
                            {line.productName}
                          </Link>
                          <button
                            type="button"
                            onClick={() => removeFromCart(line.id)}
                            aria-label="Remover item"
                            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-foreground-500 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-close-line text-[16px]"></i>
                          </button>
                        </div>
                        {line.skuLabel && (
                          <p className="text-[12px] text-foreground-600 mt-1">
                            {line.skuLabel}
                          </p>
                        )}

                        <div className="mt-2.5 flex items-center justify-between">
                          <div className="inline-flex items-center border border-background-300 rounded-full">
                            <button
                              type="button"
                              onClick={() => updateQuantity(line.id, line.quantity - 1)}
                              aria-label="Diminuir"
                              disabled={line.quantity <= 1}
                              className="w-7 h-7 flex items-center justify-center text-foreground-900 hover:bg-background-100 rounded-full cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <i className="ri-subtract-line text-[14px]"></i>
                            </button>
                            <span className="w-7 text-center text-[13px] font-medium text-foreground-950 select-none">
                              {line.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => updateQuantity(line.id, line.quantity + 1)}
                              aria-label="Aumentar"
                              disabled={atMax}
                              className="w-7 h-7 flex items-center justify-center text-foreground-900 hover:bg-background-100 rounded-full cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <i className="ri-add-line text-[14px]"></i>
                            </button>
                          </div>
                          <div className="text-right">
                            <p
                              className={`text-[14px] font-semibold ${
                                hasDiscount ? "text-primary-600" : "text-foreground-950"
                              }`}
                            >
                              {lineSymbol}
                              {formatPrice(line.unitPrice * line.quantity)}
                            </p>
                            {hasDiscount && (
                              <p className="text-[11px] text-foreground-500 line-through">
                                {lineSymbol}
                                {formatPrice(line.originalPrice * line.quantity)}
                              </p>
                            )}
                          </div>
                        </div>

                        {atMax && (
                          <p className="text-[11px] text-primary-600 mt-1.5">
                            Estoque máximo atingido
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* Limpar tudo */}
              <div className="py-3 text-center">
                <button
                  type="button"
                  onClick={onClear}
                  className="text-[12.5px] text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer inline-flex items-center gap-1.5"
                >
                  <i className="ri-delete-bin-line text-[13px]"></i>
                  Limpar carrinho
                </button>
              </div>
            </div>

            {/* Rodapé */}
            <div className="border-t border-background-200/70 px-5 py-4 bg-background-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[14px] text-foreground-700">Subtotal</span>
                <span className="font-heading text-foreground-950 text-xl">
                  {symbol}
                  {formatPrice(cartSubtotal)}
                </span>
              </div>
              <button
                type="button"
                onClick={onCheckout}
                className="w-full h-12 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[15px] font-semibold transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2"
              >
                <i className="ri-lock-line"></i>
                Finalizar compra
              </button>
              <button
                type="button"
                onClick={closeCart}
                className="w-full mt-1.5 h-9 rounded-full text-[13px] text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
              >
                Continuar comprando
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}